'use client'

import Tooltip from '@/components/design-system/tooltip'
import { Countdown } from '@/components/ui/countdown'
import { Text } from '@/components/ui/text'
import useAuth from '@/hooks/use-auth'
import { css } from '@/styled-system/css'
import { Flex } from '@/styled-system/jsx'
import { HOUR_MS } from '@/utils/time'

export default function HeaderRakebackBoost() {
  const { user } = useAuth()

  const lastRakebackBoost = user?.lastRakebackBoost

  const isRakebackBoostEnabled =
    lastRakebackBoost &&
    Date.now() < new Date(lastRakebackBoost).getTime() + HOUR_MS

  if (!isRakebackBoostEnabled) return null

  const tooltipLabel = (
    <Flex align='baseline' gap='1'>
      <Text as='span' textStyle='xs' fontWeight='bold'>
        Rakeback Boost:
      </Text>
      <Countdown
        date={new Date(lastRakebackBoost).getTime() + HOUR_MS}
        hasLabels={false}
        variant='unstyled'
        size='xs'
      />
    </Flex>
  )

  return (
    <Tooltip title={tooltipLabel}>
      <Flex
        className='group'
        align='center'
        bgImage='radial-gradient(353% 153% at 15% 10%, #D88836 0%, #DB9A5A 77%, #FABD84 100%)'
        borderRadius='lg'
        boxShadow='inset 0px 0px 0px 2px rgba(255, 255, 255, 0.15), 0px 2px 20px 0px rgba(229, 147, 61, 0.50)'
        flexShrink='0'
        justify='space-between'
        overflow='hidden'
        position='relative'
        p='2'
        h='11'
        w='46px'
        zIndex={0}
        _hover={{
          bgImage:
            'radial-gradient(353% 153% at 15% 10%, #E5923C 0%, #E29F5E 77%, #FEC38C 100%)',
        }}
      >
        <svg
          xmlns='http://www.w3.org/2000/svg'
          width='30'
          height='30'
          viewBox='0 0 30 30'
          fill='none'
          className={css({
            _groupHover: {
              animationName: 'rakeback-rocket-button',
              animationDelay: '200ms',
              animationDuration: '1500ms',
              animationIterationCount: 'infinite',
            },
          })}
        >
          <path
            fillRule='evenodd'
            clipRule='evenodd'
            d='M24.9515 0.108321L25.3602 0.159608C26.4404 0.308661 27.87 0.509002 28.6794 1.31998C29.3429 1.98191 29.5977 3.05894 29.7499 4.02057L29.8397 4.63923C30.0416 6.09771 30.0945 8.00815 29.7499 10.1302C29.0704 14.3181 26.8411 19.3154 21.2429 23.1844C21.2125 23.4857 21.2109 23.7902 21.2173 24.0947L21.2333 24.5499C21.2589 25.2503 21.2846 25.9507 21.0891 26.6302C20.7845 27.688 19.6995 28.3852 18.685 28.8853L18.1882 29.1209L17.5471 29.4029C16.3483 29.9126 14.7296 30.4351 13.7151 29.419C13.1061 28.8115 12.8561 27.9204 12.6525 27.0341L12.5772 26.7023C12.4924 26.2835 12.3854 25.8694 12.2567 25.4618C12.1765 25.2278 12.09 24.989 11.997 24.7486C11.8947 24.8742 11.7861 24.9946 11.6717 25.1092C11.1188 25.6622 10.2934 26.0484 9.61386 26.3193C8.87182 26.6126 8.03202 26.869 7.25472 27.0806L6.85565 27.1864L6.09117 27.3771L5.39881 27.5374L4.57023 27.7137L4.05256 27.8146C3.79438 27.8624 3.52845 27.8467 3.27771 27.7688C3.02696 27.6909 2.79892 27.5532 2.61326 27.3676C2.4276 27.1819 2.28989 26.9539 2.212 26.7031C2.13412 26.4523 2.1184 26.1864 2.1662 25.9282L2.30403 25.2375L2.55085 24.1188L2.74958 23.3014L2.90023 22.7244C3.11179 21.9487 3.36821 21.1088 3.66311 20.3684C3.93236 19.6872 4.3186 18.8618 4.87153 18.3089L4.99974 18.1855L4.89717 18.1438C4.62327 18.0396 4.34584 17.945 4.06538 17.8601L3.62144 17.7239C2.50918 17.3873 1.31999 17.0251 0.581152 16.2846C-0.316349 15.3887 -0.0134424 14.0248 0.419282 12.8901L0.595576 12.4509L0.879251 11.8098L1.11485 11.313C1.61488 10.3001 2.31205 9.21501 3.36982 8.91049C3.93076 8.75022 4.51413 8.7406 5.10071 8.75663L5.4533 8.76785C5.91167 8.78387 6.36843 8.8015 6.81558 8.75823C10.6845 3.15831 15.6816 0.928916 19.8694 0.24936C21.5494 -0.0261883 23.2589 -0.0736299 24.9515 0.108321ZM9.22601 20.4229C8.97377 20.2362 8.67231 20.1275 8.35896 20.1104C8.0456 20.0934 7.73412 20.1685 7.46306 20.3267L7.28677 20.4453L7.13772 20.5783L6.93739 20.8316C6.52069 21.4342 6.28349 22.2676 6.09918 23.0465L5.92609 23.7966L5.84436 24.138L6.15047 24.0643L6.82039 23.9104C7.74674 23.694 8.77406 23.4136 9.40551 22.8446C9.68007 22.5702 9.8457 22.2054 9.87164 21.8181C9.89758 21.4308 9.78206 21.0472 9.54655 20.7386L9.41513 20.5879L9.37667 20.5511L9.22601 20.4229ZM20.7381 9.24546C20.4405 8.94774 20.0871 8.71156 19.6983 8.5504C19.3094 8.38924 18.8926 8.30625 18.4716 8.30618C18.0507 8.3061 17.6339 8.38894 17.2449 8.54997C16.856 8.71099 16.5026 8.94705 16.2049 9.24466C15.9072 9.54227 15.671 9.8956 15.5099 10.2845C15.3487 10.6734 15.2657 11.0902 15.2656 11.5111C15.2656 11.9321 15.3484 12.349 15.5094 12.7379C15.6704 13.1268 15.9065 13.4803 16.2041 13.778C16.8051 14.3792 17.6204 14.7171 18.4705 14.7172C19.3206 14.7174 20.136 14.3798 20.7373 13.7788C21.3385 13.1777 21.6764 12.3624 21.6765 11.5123C21.6767 10.6621 21.3391 9.84672 20.7381 9.24546Z'
            fill='url(#paint0_linear_3557_3153)'
          />
          <defs>
            <linearGradient
              id='paint0_linear_3557_3153'
              x1='6.81971'
              y1='2.75473'
              x2='29.606'
              y2='24.4724'
              gradientUnits='userSpaceOnUse'
            >
              <stop stopColor='#FFEAD7' />
              <stop offset='0.32' stopColor='#FFC181' />
              <stop offset='1' stopColor='#FFE4C7' />
            </linearGradient>
          </defs>
        </svg>
      </Flex>
    </Tooltip>
  )
}
